/**
 * natural_types — the vocabulary of the NATURAL OBJECT layer (2026-10-07). PURE (no three, no DOM).
 *
 * The owner's catalog (atlas/docs/research/2026-10-07-space-objects/catalog-natural-space-objects.md) lists 120 object
 * types, 11 transient events and 35 combined systems. This layer realises the ones that live at the scale of a star
 * system and its sky; galaxies and anything larger are excluded (natural_catalog.ts says what became of each entry).
 *
 * THE LAYER IS PRESENTATION. A star system's LAYOUT — its star's footprint radius, planet orbits, belts, dens, station,
 * arrival lane — is `shared/galaxy.ts`, mirrored on the server, and is NOT changed here: `systemMakeup()` only decides
 * WHAT each of those things is (a K dwarf, a lava world, a carbonaceous belt) and which extra natural bodies stand in
 * the gaps (moons, comets, discs, a companion star inside the footprint). Nothing here is server state.
 */

/** Stars, substellar objects and compact remnants (catalog ST-01…19, CR-01…05). */
export type StarKind =
  | 'm_dwarf' | 'k_dwarf' | 'g_dwarf' | 'f_dwarf' | 'a_star' | 'ob_star'
  | 'red_giant' | 'agb' | 'post_agb' | 'carbon_star' | 'mira' | 'cepheid'
  | 'red_supergiant' | 'blue_supergiant' | 'lbv' | 'wolf_rayet' | 'be_star'
  | 'protostar' | 't_tauri' | 'herbig'
  | 'brown_dwarf' | 'white_dwarf' | 'neutron_star' | 'pulsar' | 'magnetar' | 'black_hole';

/** How the system's stars are arranged (catalog SY-12…25). */
export type SystemConfig =
  | 'single'
  | 'detached_binary'   // SY-12 (+ SY-15: every close pair here is circumbinary — the planets orbit outside the footprint)
  | 'semidetached'      // SY-13: a donor overflowing onto a white dwarf (dwarf novae EV-02, a rare nova EV-01)
  | 'contact_binary'    // SY-14
  | 'wide_binary'       // SY-16: a second sun far outside the lanes
  | 'hierarchical_triple' // SY-17: a close pair + a far third
  | 'young_binary'      // SY-18: two young stars, discs and jets
  | 'colliding_wind'    // SY-19: Wolf–Rayet + O/B, a dust pinwheel
  | 'be_xray'           // SY-20: a Be star's disc feeding a neutron star
  | 'symbiotic'         // SY-21: a giant feeding a white dwarf (recurrent nova)
  | 'wd_debris'         // SY-22: a white dwarf inside a disc of disrupted planets
  | 'xray_binary';      // SY-23: a neutron star or black hole + a donor

export type SystemAge = 'forming' | 'young' | 'mature' | 'old' | 'dead';

export interface NaturalStar { kind: StarKind; seed: number }

/** Planets (catalog PL-01…15). The kind is consistent with the planet's pooled FACE (0 desert, 1 gas, 2 ice, 3 moon, 4 ocean). */
export type PlanetKind =
  | 'airless' | 'desert' | 'temperate' | 'water' | 'greenhouse' | 'frozen' | 'lava'
  | 'sub_neptune' | 'gas_giant' | 'hot_giant' | 'ultra_hot_giant' | 'ice_giant' | 'super_puff';

/** Moons (catalog PL-18…23). */
export type MoonKind = 'rocky' | 'volcanic' | 'icy_ocean' | 'plume' | 'hazy' | 'irregular';

export interface MoonMakeup {
  kind: MoonKind;
  seed: number;
  /** Orbit radius about its planet's centre (m), its inclination to the system plane (rad) and node (rad). */
  orbitM: number; inc: number; node: number;
  /** One orbit in real seconds; the phase (rad) at wall-clock 0; −1 = retrograde. */
  periodS: number; phase0: number; sense: 1 | -1;
  /** The moon's radius (m). */
  radiusM: number;
  /** A small moon keeping a ring's edge (SY-05) rather than a world of its own. */
  shepherd?: boolean;
}

export interface PlanetMakeup {
  /** Index into `systemSector(sys).planets`. */
  index: number;
  kind: PlanetKind;
  face: number;
  seed: number;
  /** Starlight at the planet relative to a G dwarf at 600 km (the kind's driver; > 6 scorched, < 0.08 frigid). */
  heat: number;
  /** PL-08: a rocky world in the largest size band (a descriptor, not a look of its own). */
  superEarth: boolean;
  /** PL-15: it is losing its envelope — a gas tail away from the star. */
  evaporating: boolean;
  /** NE-21: a giant with visible aurorae (and a plasma torus when a volcanic moon feeds it). */
  aurora: boolean;
  /** NE-09: a forming giant still inside its own disc (young systems only). */
  circumplanetary: boolean;
  /** PL-24: it wears the derived ring (rings.derivedRingFor) — its regular moons then orbit in the ring's plane, outside it. */
  ringed: boolean;
  moons: MoonMakeup[];
}

/** Small-body populations (catalog SB-01…05): what a belt is made of. */
export type FieldTaxonomy = 'carbonaceous' | 'stony' | 'metallic' | 'icy';
export type FieldLandmark = 'contact_binary' | 'binary_asteroid' | 'active_asteroid' | 'rubble_pile';
export interface FieldMakeup {
  /** Index into `systemSector(sys).fields`. */
  index: number;
  taxonomy: FieldTaxonomy;
  /** SB-04 / SB-05: loose aggregates (boulder-strewn, top-shaped) or coherent fragments (angular). */
  structure: 'rubble' | 'coherent';
  /** At most one per system: a named body standing just outside the belt. */
  landmark: { kind: FieldLandmark; pos: [number, number, number]; radiusM: number; seed: number } | null;
}

/** A Keplerian path about the star at the origin (game scale; the body's place is a function of wall-clock time). */
export interface OrbitMakeup {
  /** Periapsis and apoapsis distance (m). */
  q: number; Q: number;
  /** Inclination to the system plane, longitude of the node, argument of periapsis (rad). */
  inc: number; node: number; arg: number;
  /** One orbit in real seconds; the mean anomaly (rad) at wall-clock 0. */
  periodS: number; phase0: number;
}

export type FeatureMakeup =
  /** SB-09 (an active comet), SB-08 (a main-belt comet: a low-eccentricity orbit, faint activity) or SB-10 (dormant: no tail). */
  | { kind: 'comet'; variant: 'active' | 'main_belt' | 'dormant'; seed: number; /** null = it stands at `fixed` (the Commons Reach's two authored comets). */ orbit: OrbitMakeup | null; fixed: [number, number, number] | null; nucleusM: number }
  /** SB-11: a dark body among the giant planets, sometimes with a faint coma. */
  | { kind: 'centaur'; seed: number; orbit: OrbitMakeup; radiusM: number; active: boolean }
  /** SB-12 + SY-10: a sparse outer belt of icy bodies; PL-17 + SY-06: its dwarf planet and that world's large moon. */
  | { kind: 'tno_belt'; seed: number; radiusM: number; widthM: number; count: number; dwarf: { pos: [number, number, number]; radiusM: number; moonRadiusM: number; moonOrbitM: number } | null }
  /** SY-08: two swarms sharing a giant planet's orbit, 60° ahead and behind. */
  | { kind: 'trojans'; seed: number; planet: number; swarms: Array<{ pos: [number, number, number]; radiusM: number; count: number }> }
  /** SB-15: the zodiacal dust of the inner system — a faint band of scattered light in the plane. */
  | { kind: 'dust_band'; seed: number; innerM: number; outerM: number; strength: number }
  /** NE-10: a debris disc — a collisional ring beyond the planets. */
  | { kind: 'debris_disc'; seed: number; innerM: number; outerM: number }
  /** NE-08: the gas-and-dust disc of a forming system, with gaps at its planets' orbits. */
  | { kind: 'proto_disc'; seed: number; innerM: number; outerM: number; gapsM: number[] }
  /** PL-16: a starless world passing far outside the lanes. */
  | { kind: 'rogue'; seed: number; pos: [number, number, number]; radiusM: number }
  /** SB-14: the pebbles a comet has shed along its orbit. */
  | { kind: 'meteoroid_stream'; seed: number; orbit: OrbitMakeup; count: number }
  /** SB-13: a body from another star passing straight through, never to return: it crosses from `from` to `to` in `periodS`
   *  seconds of wall clock (place = from + (to − from) × frac((t / periodS) + phase0)); the next crossing is the next visitor. */
  | { kind: 'interstellar'; seed: number; from: [number, number, number]; to: [number, number, number]; periodS: number; phase0: number; lengthM: number };

/** Star clusters of the sky (catalog SC-01…05): far beyond the system, a landmark in one direction. */
export type SkyClusterKind = 'open_cluster' | 'globular_cluster' | 'young_cluster' | 'embedded_cluster' | 'association';
export interface SkyMakeup {
  kind: SkyClusterKind;
  seed: number;
  /** Unit direction in SECTOR axes, and the angular radius (rad). */
  dir: [number, number, number];
  angR: number;
}

/** The engine's five nebula kinds (enginex/render/space/nebula). */
export type SkyNebulaKind = 'emission' | 'reflection' | 'dark' | 'planetary' | 'remnant';

export interface SystemMakeup {
  systemId: string;
  /** The showpiece this system hosts ('' = an ordinary system). One of SHOWPIECES' keys. */
  showpiece: string;
  age: SystemAge;
  /** The layout's star radius (m): everything stellar — the primary, a close companion, an accretion stream, an inner
   *  disc — is drawn INSIDE `footprintM × STELLAR_REACH`; the station rides at 1.4 footprints + 50 km. */
  footprintM: number;
  config: SystemConfig;
  primary: NaturalStar;
  /** A close companion sharing the footprint (binaries). */
  companion: NaturalStar | null;
  /** A wide companion far outside the lanes (absolute sector metres). */
  distant: (NaturalStar & { pos: [number, number, number] }) | null;
  /** One per planet of the sector, in the sector's order. Empty for sys0 (its planets are authored). */
  planets: PlanetMakeup[];
  /** One per belt of the sector, in the sector's order. Empty for sys0. */
  fields: FieldMakeup[];
  features: FeatureMakeup[];
  sky: SkyMakeup[];
  /** The kind of this system's distant nebula: a kind = it has one of that kind; null = it has none; undefined = the
   *  older rule (nebula_look.skyNebulaeFor) decides. */
  skyNebula?: SkyNebulaKind | null;
}

/** Stellar things stay inside this many footprint radii of the origin (the station's seat begins at 1.4 + 50 km). */
export const STELLAR_REACH = 1.25;
